import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";

/** Foto-/PDF-Ansicht einer gespeicherten Datei – eingepasst, mit Zoom, Drehen und Seiten (gleicher Viewer wie im Posteingang). */
export const DocPreviewDialog = ({ open, onClose, url, title, mimeType, testid = "doc-preview-dialog" }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [type, setType] = useState(mimeType || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !url) return undefined;
    let revoke = null;
    setLoading(true);
    apiClient.get(url, { responseType: "blob" })
      .then((r) => {
        const ct = r.headers?.["content-type"] || mimeType || "application/pdf";
        setType(ct);
        revoke = URL.createObjectURL(new Blob([r.data], { type: ct }));
        setBlobUrl(revoke);
      })
      .catch(() => { toast.error("Vorschau konnte nicht geladen werden"); onClose(); })
      .finally(() => setLoading(false));
    return () => { if (revoke) URL.revokeObjectURL(revoke); setBlobUrl(null); };
  }, [open, url, mimeType, onClose]);

  const download = async () => {
    try {
      const r = await apiClient.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = title || "dokument"; a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(href); }, 2000);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[92vw] xl:max-w-6xl max-h-[94vh] overflow-hidden flex flex-col" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="truncate text-base pr-8 flex items-center justify-between gap-2">
            <span className="truncate">{title || "Dokument"}</span>
            <Button size="sm" variant="ghost" onClick={download} data-testid={`${testid}-download`} title="Original herunterladen"><Download className="h-4 w-4" /></Button>
          </DialogTitle>
        </DialogHeader>
        <div className="h-[76vh] bg-slate-100 rounded-md border border-slate-200 overflow-hidden flex">
          {loading || !blobUrl ? (
            <Loader2 className="m-auto h-6 w-6 animate-spin text-slate-500" />
          ) : (
            <DocumentViewer blobUrl={blobUrl} contentType={type} fileName={title} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
